import { useEffect, useState } from 'react';
import IconBack from '../icons/back';
import IconLoader from '../icons/loader';
import IconUp from '../icons/up';
import IconDown from '../icons/down';
import {
  BaseellaStatus,
  BaseellaStatusTitle,
  EnumExchangeTabs,
  EnumNostroTabs,
  EnumTabs,
  InternalEnumTabs,
  TransactionStatusChangeTypes,
} from './types';
import StatusItem from './status-item';

import styles from './transaction.module.css';

export default function TransactionStatusChange({
  transaction,
  onRollbackStatus,
  onNextStatus,
  updateStatusIsLoading,
  isLoadingStatuses,
  latestQueuedStatus,
  baseellaStatus,
  calcError,
  activeTab,
}: TransactionStatusChangeTypes): React.JSX.Element {
  const [collapsed, setCollapsed] = useState(false);
  const [collapsedConditions, setCollapsedConditions] = useState(false);
  const [currentBaseellaStatus, setCurrentBaseellaStatus] = useState('');

  useEffect(() => {
    if (baseellaStatus) {
      setCurrentBaseellaStatus(baseellaStatus);
    }
  }, [baseellaStatus]);

  const generalTab = Boolean(
    activeTab === EnumTabs.GENERAL ||
    activeTab === EnumNostroTabs.GENERAL ||
    activeTab === EnumExchangeTabs.GENERAL ||
    activeTab === InternalEnumTabs.GENERAL,
  );
  const orderTab = activeTab === EnumTabs.ORDER;
  const warningsTab =
    activeTab === EnumTabs.WARNING || activeTab === InternalEnumTabs.WARNING;

  return (
    <div className={styles.statusBlock}>
      {updateStatusIsLoading && (generalTab || orderTab || warningsTab) && (
        <div className={styles.statusUpdateInProgress}>
          <IconLoader />
        </div>
      )}
      {transaction.prevStatus &&
        (generalTab || orderTab || warningsTab) &&
        onRollbackStatus &&
        transaction.prevStatus.map((item) => {
          let buttonText = 'Roll back status';
          let buttonIcon = <IconBack />;

          if (!item.allowed) {
            buttonText = 'Conditions are not met';

            buttonIcon = collapsedConditions ? (
              <IconUp className={styles.arrowIcon} />
            ) : (
              <IconDown className={styles.arrowIcon} />
            );
          }

          return (
            <button
              type="button"
              className={`${styles.statusChangeWrapper} ${styles.rollBackStatusChange}`}
              onClick={() => {
                if (item.allowed) {
                  onRollbackStatus(item.baseellaStatusNext);
                } else {
                  setCollapsedConditions(!collapsedConditions);
                }
              }}
              key={item.baseellaStatusNext}
            >
              <div
                className={`${styles.rollBackStatusContent} ${collapsedConditions ? styles.withPaddingTop : ''}`}
              >
                <div className={styles.statusChangeValue}>
                  {BaseellaStatusTitle[item.baseellaStatusNext]}
                </div>
                <div
                  className={`${styles.changeStatusButton} ${styles.changeStatusButtonRollback} ${!item.allowed ? styles.redText : ''}`}
                >
                  {buttonText}
                  {buttonIcon}
                </div>
              </div>

              {collapsedConditions && (
                <>
                  <ul className={styles.errorsList}>
                    {item?.errors?.map((error, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={`${error}-${index}`}>{error}</li>
                    ))}
                  </ul>
                  {item?.warnings && item?.warnings?.length > 0 && (
                    <ul
                      className={`${styles.warningsWrapper} ${styles.errorsWranings}`}
                    >
                      {item?.warnings?.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </button>
          );
        })}
      <div className={styles.statusChangeButtonWrapper}>
        <button
          type="button"
          className={`${styles.statusChangeWrapper} ${
            styles.statusChangeWrapperCurrent
          } ${
            latestQueuedStatus &&
            currentBaseellaStatus &&
            currentBaseellaStatus !== latestQueuedStatus
              ? styles.withCollapse
              : ''
          }`}
          onClick={() =>
            latestQueuedStatus &&
            currentBaseellaStatus &&
            currentBaseellaStatus !== latestQueuedStatus
              ? setCollapsed(!collapsed)
              : undefined
          }
        >
          <div className={styles.statusChangeValue}>
            {
              BaseellaStatusTitle[
                currentBaseellaStatus === latestQueuedStatus ||
                !latestQueuedStatus
                  ? (currentBaseellaStatus as BaseellaStatus)
                  : latestQueuedStatus
              ]
            }
          </div>
          <div
            className={`${styles.changeStatusButton} ${
              latestQueuedStatus && currentBaseellaStatus !== latestQueuedStatus
                ? styles.color
                : ''
            }`}
          >
            {currentBaseellaStatus === latestQueuedStatus || !latestQueuedStatus
              ? 'Current status'
              : 'Target status'}
            {latestQueuedStatus &&
              currentBaseellaStatus !== latestQueuedStatus &&
              (!collapsed ? <IconDown /> : <IconUp />)}
          </div>
        </button>
        {collapsed && (
          <ul className={styles.statusesList}>
            <li>
              <div className={styles.statusListItemWrapper}>
                <div
                  className={`${styles.statusListItem} ${styles.currentStatus}`}
                >
                  {BaseellaStatusTitle[currentBaseellaStatus as BaseellaStatus]}
                </div>
                <div className={styles.statusHint}>Current status</div>
              </div>
            </li>
            <li>
              <div className={styles.statusListItemWrapper}>
                <div className={styles.statusListItem}>
                  {BaseellaStatusTitle[latestQueuedStatus as BaseellaStatus]}
                </div>
                <div className={styles.statusHint}>Target status</div>
              </div>
            </li>
          </ul>
        )}
      </div>

      {transaction.nextStatus &&
        onNextStatus &&
        (generalTab || orderTab || warningsTab) &&
        transaction.nextStatus.map((item) => (
          <StatusItem
            key={item.baseellaStatusNext}
            item={item}
            onStatusTransition={onNextStatus}
            isLoadingStatuses={isLoadingStatuses}
            updateStatusIsLoading={updateStatusIsLoading}
            disabled={Boolean(calcError)}
          />
        ))}
      {calcError && (
        <div
          className={styles.statusCalcError}
        >{`You can't change transaction status because: ${calcError}`}</div>
      )}
    </div>
  );
}
